/**
 * Timelog Hooks
 * =============
 * React Query hooks for timelog time tracking operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { timelogApi } from '@/services/api'
import type {
  TimelogCustomerCreate,
  TimelogCustomerUpdate,
  TimelogEntryCreate,
  TimelogEntryUpdate,
  TimelogSearchParams,
} from '@/types'

export const timelogKeys = {
  all: ['timelog'] as const,
  customers: () => [...timelogKeys.all, 'customers'] as const,
  entries: () => [...timelogKeys.all, 'entries'] as const,
  entryList: (params?: TimelogSearchParams) => [...timelogKeys.entries(), params] as const,
}

// =============================================================================
// Customer Hooks
// =============================================================================

export function useTimelogCustomers() {
  return useQuery({
    queryKey: timelogKeys.customers(),
    queryFn: () => timelogApi.listCustomers(),
  })
}

export function useCreateTimelogCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: TimelogCustomerCreate) => timelogApi.createCustomer(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: timelogKeys.customers() }),
  })
}

export function useUpdateTimelogCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: TimelogCustomerUpdate }) =>
      timelogApi.updateCustomer(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: timelogKeys.customers() }),
  })
}

export function useDeleteTimelogCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => timelogApi.deleteCustomer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: timelogKeys.customers() }),
  })
}

// =============================================================================
// Entry Hooks
// =============================================================================

export function useTimelogEntries(params?: TimelogSearchParams) {
  return useQuery({
    queryKey: timelogKeys.entryList(params),
    queryFn: () => timelogApi.listEntries(params),
  })
}

export function useCreateTimelogEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: TimelogEntryCreate) => timelogApi.createEntry(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timelogKeys.all })
    },
  })
}

export function useUpdateTimelogEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: TimelogEntryUpdate }) =>
      timelogApi.updateEntry(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timelogKeys.all })
    },
  })
}

export function useDeleteTimelogEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => timelogApi.deleteEntry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timelogKeys.all })
    },
  })
}
